import { isBlank } from "@/utils/is";

/**
 * 数値タイプEnum
 */
export enum NumberType {
  INTEGER = "INTEGER",
  DECIMAL = "DECIMAL",
}

/**
 * [type="text"]バリデーション用引数Interface
 */
interface ValidateTextArgs {
  t: "text";
  minLength?: number; // 最小文字数
  maxLength?: number; // 最大文字数
  regexp?: RegExp; // 正規表現パターン
  required: boolean; // 必須チェックの有無
  requiredMessage: string; // 必須エラーメッセージ
  valueMessage: string; // 値不正エラーメッセージ
  regexpMessage?: string; // 正規表現パターンエラーメッセージ
}

/**
 * [type="number"]バリデーション用引数Interface
 */
interface ValidateNumberArgs {
  t: "number";
  numberType: NumberType; // 数値の型
  min?: number; // 最小値
  max: number; // 最大値
  digit?: number; // 小数点以下桁数
  required: boolean; // 必須チェックの有無
  requiredMessage: string; // 必須メッセージ
  valueMessage: string; // 値不正エラーメッセージ
  typeMessage: string; // 型不正エラーメッセージ
  greaterThen?: number; // それより大きい
}

export type ValidateType = ValidateTextArgs | ValidateNumberArgs;

/**
 * [type="text"]チェック
 */
const validateText = (
  value: string | number | null | undefined,
  required: boolean,
  requiredMessage: string,
  valueMessage: string,
  minLength?: number,
  maxLength?: number,
  regexp?: RegExp,
  regexpMessage?: string
): string => {
  if (isBlank(value)) {
    if (required) {
      return requiredMessage;
    }
    return "";
  }

  const tmpValue = `${value}`;

  // 文字数チェック
  if (minLength && maxLength && (tmpValue!.length < minLength || maxLength < tmpValue!.length)) {
    // 範囲
    return valueMessage;
  }

  // 最小
  if (minLength && tmpValue!.length < minLength) {
    return valueMessage;
  }

  // 最大
  if (maxLength && tmpValue!.length > maxLength) {
    return valueMessage;
  }

  if (regexp && !regexp.test(tmpValue)) {
    return regexpMessage || "値が不正です";
  }

  return "";
};

/**
 * [type="number"]チェック
 */
const validateNumber = (
  numberType: NumberType, // 数値タイプ
  value: string | number | null | undefined,
  required: boolean,
  requiredMessage: string,
  valueMessage: string,
  typeMessage: string,
  min?: number,
  max?: number,
  digit?: number,
  greaterThen?: number
): string => {
  if (isBlank(value)) {
    if (required) {
      return requiredMessage;
    }
    return "";
  }

  const tmpValue = `${value}`;

  // 整数チェック
  if (numberType === NumberType.INTEGER && isBlank(tmpValue.match(/^(-?([1-9]\d*|0))$/))) {
    return typeMessage;
  }

  // 浮動小数点数チェック
  if (numberType === NumberType.DECIMAL && isBlank(tmpValue.match(/^-?([1-9]\d*|0)(\.(\d+))?$/))) {
    return typeMessage;
  }

  const decimal = tmpValue.split(".")[1];

  // 浮動小数点桁数チェック
  if (numberType === NumberType.DECIMAL && decimal && digit) {
    if (decimal!.length > digit) {
      return `小数点第${digit}位以内で入力してください`;
    }
  }

  const tmpNum = +tmpValue;

  if (greaterThen && max && (tmpNum <= greaterThen || tmpNum > max)) {
    return valueMessage;
  }

  // 数値範囲チェック
  if (min && max && (tmpNum < min! || max! < tmpNum)) {
    return valueMessage;
  }
  // 最小
  if (min && tmpNum < min!) {
    return valueMessage;
  }
  // 最大
  if (max && tmpNum > max!) {
    return valueMessage;
  }

  return "";
};

const validator = (validate: ValidateType, value: any) => {
  if (validate.t === "text") {
    return validateText(
      value,
      validate.required,
      validate.requiredMessage,
      validate.valueMessage,
      validate.minLength,
      validate.maxLength,
      validate.regexp,
      validate.regexpMessage
    );
  }
  if (validate.t === "number") {
    return validateNumber(
      validate.numberType,
      value,
      validate.required,
      validate.requiredMessage,
      validate.valueMessage,
      validate.typeMessage,
      validate.min,
      validate.max,
      validate.digit,
      validate.greaterThen
    );
  }
  throw new Error("validate type error");
};

export const validateProperty = {
  name: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 40,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "40文字以内で入力してください",
      },
      value
    ),
  pref: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 10,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "10文字以内で入力してください",
      },
      value
    ),
  city: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 25,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "25文字以内で入力してください",
      },
      value
    ),
  town: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 50,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "50文字以内で入力してください",
      },
      value
    ),
  street: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 100,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "100文字以内で入力してください",
      },
      value
    ),
  line: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 25,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "25文字以内で入力してください",
      },
      value
    ),
  station: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 25,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "25文字以内で入力してください",
      },
      value
    ),
  bus: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.INTEGER,
        min: 1,
        max: 999,
        required: false,
        requiredMessage: "必須項目になります",
        valueMessage: "1~999の範囲で入力してください",
        typeMessage: "整数を入力してください",
      },
      value
    ),
  walk: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.INTEGER,
        min: 1,
        max: 999,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "1~999の範囲で入力してください",
        typeMessage: "半角整数を入力してください",
      },
      value
    ),
  builtDateYear: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  builtDateMonth: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  totalStory: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.INTEGER,
        min: 1,
        max: 1000,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "1~999の範囲で入力してください",
        typeMessage: "半角整数を入力してください",
      },
      value
    ),
  structure: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 25,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "25文字以内で入力してください",
      },
      value
    ),
};

export const validatePropertyDetail = {
  story: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.INTEGER,
        min: 1,
        max: 200,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "1~200の範囲で入力してください",
        typeMessage: "半角整数を入力してください",
      },
      value
    ),
  roomNumber: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 10,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "10文字以内で入力してください",
      },
      value
    ),
  unitSize: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.DECIMAL,
        greaterThen: 10,
        min: undefined,
        max: 499.99,
        digit: 2,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "10.01~499.99の範囲で入力してください",
        typeMessage: "半角数値を入力してください",
      },
      value
    ),
  balconyDirection: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  landSize: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.DECIMAL,
        min: 1,
        max: 9999.99,
        digit: 2,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "1~9999.99の範囲で入力してください",
        typeMessage: "半角数値を入力しください",
      },
      value
    ),
  undergroundStory: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.INTEGER,
        min: 1,
        max: 200,
        required: false,
        requiredMessage: "必須項目になります",
        valueMessage: "1~200の範囲で入力してください",
        typeMessage: "半角整数を入力してください",
      },
      value
    ),
  totalStory: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.INTEGER,
        min: 1,
        max: 200,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "1~200の範囲で入力してください",
        typeMessage: "半角整数を入力してください",
      },
      value
    ),
  structure: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  houseSize: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.DECIMAL,
        min: 1,
        max: 9999.99,
        digit: 2,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "1~9999.99の範囲で入力してください",
        typeMessage: "半角数値を入力してください",
      },
      value
    ),
  floorPlan: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
};

export const validateConnecting = {
  status: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  type: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  direction: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  widthGroup: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  contactWidthGroup: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  width: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.DECIMAL,
        min: 0,
        max: 999.9,
        digit: 1,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "0~999.9の範囲で入力してください",
        typeMessage: "正しい値を入力しください",
      },
      value
    ),
  contactWidth: (value: any) =>
    validator(
      {
        t: "number",
        numberType: NumberType.DECIMAL,
        min: 0,
        max: 999.9,
        digit: 1,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "0~999.9の範囲で入力してください",
        typeMessage: "正しい値を入力しください",
      },
      value
    ),
};

export const validateCustomer = {
  lastName: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 20,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "20文字以内で入力してください",
      },
      value
    ),
  firstName: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 20,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "20文字以内で入力してください",
      },
      value
    ),
  lastNameKana: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 20,
        required: true,
        regexp: /^[ァ-ヶー]+$/,
        requiredMessage: "必須項目になります",
        valueMessage: "20文字以内で入力してください",
        regexpMessage: "カナで入力してください",
      },
      value
    ),
  firstNameKana: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 20,
        required: true,
        regexp: /^[ァ-ヶー]+$/,
        requiredMessage: "必須項目になります",
        valueMessage: "20文字以内で入力してください",
        regexpMessage: "カナで入力してください",
      },
      value
    ),
  mailAddress: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 255,
        required: true,
        regexp: /^[A-Za-z0-9]{1}[A-Za-z0-9_.-]*@{1}[A-Za-z0-9_.-]{1,}\.[A-Za-z0-9]{1,}$/,
        requiredMessage: "必須項目になります",
        valueMessage: "255文字以内で入力してください",
        regexpMessage: "メールアドレスの形式が不正です",
      },
      value
    ),
  tel: (value: any, required: boolean) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 100,
        required,
        regexp: /^[0-9-]+$/,
        requiredMessage: "必須項目になります",
        valueMessage: "100文字以内で入力してください",
        regexpMessage: "電話番号の形式が不正です",
      },
      value
    ),
  postCode: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 100,
        required: true,
        regexp: /^[0-9]{3}-[0-9]{4}$/,
        requiredMessage: "必須項目になります",
        valueMessage: "100文字以内で入力してください",
        regexpMessage: "郵便番号は111-1111の形式で入力してください",
      },
      value
    ),
  pref: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 10,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "10文字以内で入力してください",
      },
      value
    ),
  city: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 25,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "25文字以内で入力してください",
      },
      value
    ),
  town: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 50,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "50文字以内で入力してください",
      },
      value
    ),
  street: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 100,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "100文字以内で入力してください",
      },
      value
    ),
  building: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 100,
        required: false,
        requiredMessage: "必須項目になります",
        valueMessage: "100文字以内で入力してください",
      },
      value
    ),
  salePeriod: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 100,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "100文字以内で入力してください",
      },
      value
    ),
  relation: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 100,
        required: false,
        requiredMessage: "必須項目になります",
        valueMessage: "100文字以内で入力してください",
      },
      value
    ),
  shopPrefId: (value: any) =>
    validator(
      {
        t: "text",
        minLength: 0,
        maxLength: 10,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "10文字以内で入力してください",
      },
      value
    ),
  shopId: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
  referralSourceCd: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
};

export const inquiryValuation = {
  saleReason: (value: any) =>
    validator(
      {
        t: "text",
        minLength: undefined,
        maxLength: undefined,
        required: true,
        requiredMessage: "必須項目になります",
        valueMessage: "",
      },
      value
    ),
};

export default validator;
